// Photo and video provenance capture for Casavio uploads.
// All errors are swallowed and logged — provenance failure must never block the user's upload.
//
// Casavio Design Principle #12 (Photo metadata): no Casavio photo retains
// location coordinates or device fingerprints. Commit L removed the
// EXIF-derived columns (gps_*, device_*, exif_taken_at, exif_raw) from
// the photo_provenance table and stopped reading EXIF here. Remaining
// provenance is hash + size + upload time + resource linkage — useful for
// dedup, fraud signal, and audit, with no metadata-retention concern.

import { supabase } from '../supabase'

export async function computeSHA256(file) {
  if (!file) return null
  try {
    const buffer = await file.arrayBuffer()
    const digest = await crypto.subtle.digest('SHA-256', buffer)
    const bytes = new Uint8Array(digest)
    let hex = ''
    for (let i = 0; i < bytes.length; i++) {
      hex += bytes[i].toString(16).padStart(2, '0')
    }
    return hex
  } catch (err) {
    console.warn('[Provenance] computeSHA256 failed', err)
    return null
  }
}

// Resolve a home address only when needed; never blocks the user.
async function resolveHomeAddress(homeId) {
  if (!homeId) return null
  try {
    const { data } = await supabase.from('homes').select('address').eq('id', homeId).single()
    return data?.address || null
  } catch {
    return null
  }
}

function detectMediaKind(mime) {
  if (!mime) return null
  if (mime.startsWith('image/')) return 'photo'
  if (mime.startsWith('video/')) return 'video'
  return null
}

/**
 * Insert a photo_provenance row. Fire-and-forget; returns the inserted row or null.
 *
 * @param {Object} args
 * @param {File|Blob} args.file              The bytes that were uploaded to storage (post-strip, post-compression).
 * @param {File}     [args.originalFile]     Optional original user file — used for original_file_hash.
 *                                            If omitted, falls back to `file` for both.
 * @param {string}   args.storageBucket
 * @param {string}   args.storagePath
 * @param {'appliance'|'system'|'document'|'room'|'home'|'video'} args.resourceType
 * @param {string}   [args.resourceId]
 * @param {string}   [args.homeId]
 * @param {string}   args.userId
 * @param {string}   [args.homeAddressSnapshot]  If omitted, looked up from homes table when homeId is set.
 */
export async function recordProvenance({
  file,
  originalFile,
  storageBucket,
  storagePath,
  resourceType,
  resourceId,
  homeId,
  userId,
  homeAddressSnapshot,
}) {
  try {
    if (!file || !userId || !storageBucket || !storagePath || !resourceType) {
      console.warn('[Provenance] missing required field — skipping insert')
      return null
    }

    const mime = file.type || originalFile?.type || ''
    const media_kind = detectMediaKind(mime)
    if (!media_kind) {
      console.warn('[Provenance] unsupported media_type, skipping:', mime)
      return null
    }

    const [sha256_hash, original_file_hash] = await Promise.all([
      computeSHA256(file),
      originalFile ? computeSHA256(originalFile) : Promise.resolve(null),
    ])

    if (!sha256_hash) {
      console.warn('[Provenance] hash failed — skipping insert')
      return null
    }

    const address = homeAddressSnapshot !== undefined ? homeAddressSnapshot : await resolveHomeAddress(homeId)

    const row = {
      user_id: userId,
      home_id: homeId || null,
      storage_bucket: storageBucket,
      storage_path: storagePath,
      resource_type: resourceType,
      resource_id: resourceId || null,
      media_type: mime,
      media_kind,
      file_size_bytes: file.size ?? null,
      sha256_hash,
      original_file_hash: original_file_hash || null,
      original_file_size_bytes: originalFile?.size ?? null,
      // No EXIF-derived value to consult; the timestamp lineage is
      // server-only since Commit L removed the EXIF read path.
      timestamp_source: 'server',
      home_address_snapshot: address,
    }

    const { data, error } = await supabase.from('photo_provenance').insert(row).select().single()
    if (error) {
      console.warn('[Provenance] insert failed', error)
      return null
    }
    return data
  } catch (err) {
    console.warn('[Provenance]', err)
    return null
  }
}
